import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  TextField,
  AutocompleteRenderInputParams,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import {
  Controller,
  useForm,
  FieldError,
  Merge,
  FieldErrorsImpl,
} from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { generateReport } from "../../../../api/services/api";
import { AxiosResponse, AxiosError } from "axios";

interface ReportFormData {
  medicalRecordNumber: string;
  mainConcern: string;
  procedures: string | null;
  findings: string;
  behavior: string | null;
  attention: string | null;
  eyeContact: string | null;
  howProcedure: string;
  reinforcement: string;
  generalImpression: string;
  recommendations: string;
  planNextSession: string;
}

interface GenerateReportPayload {
  data: ReportFormData & { date: string };
  type: "MANUAL";
  sessionId: string | number;
}

interface GenerateReportSuccessResponse {
  message?: string;
}

interface ApiErrorData {
  message?: string | string[];
}

interface GenerateReportFormProps {
  toggleDrawer: (isOpen: boolean) => void;
  data?: {
    id: string | number;
    patientProfile?: {
      fullName?: string;
      dob?: string;
      age?: string | number;
      city?: string;
    };
    sessionId?: string;
    medicalRecordNumber?: string;
  };
}

// Update the generateReportId function
const generateReportId = (clientId?: string | number) => {
  const now = new Date();
  const date = now.toISOString().slice(0, 10).replace(/-/g, ""); // YYYYMMDD format
  const time = now.toTimeString().slice(0, 8).replace(/:/g, ""); // HHMMSS format
  const client = clientId || "UNK"; // Use 'UNK' if clientId is not available

  return `${date}-${time}-${client}`;
};

export const GenerateReportForm = ({
  toggleDrawer,
  data,
}: GenerateReportFormProps) => {
  const queryClient = useQueryClient();
  const [reportDate, setReportDate] = useState<Dayjs | null>(dayjs());

  // Get client ID from the data - use data.id which represents the session/patient ID
  const clientId = data?.id || "UNK";

  // Use the new format: date-time-clientId
  const autoGeneratedMRN =
    data?.medicalRecordNumber || generateReportId(clientId);

  const sessionId = data?.id;

  let patientDob: Dayjs | null = null;
  const dobString = data?.patientProfile?.dob;
  console.log("Raw DOB string from props:", dobString);

  if (dobString && typeof dobString === "string" && dobString.trim() !== "") {
    // Attempt 1: Try parsing with default dayjs (handles ISO and some common formats)
    const parsedDate = dayjs(dobString);

    if (parsedDate.isValid()) {
      patientDob = parsedDate;
    } else {
      // Attempt 2: Try parsing with "MM/DD/YYYY"
      const parsedUsDate = dayjs(dobString, "MM/DD/YYYY");
      if (parsedUsDate.isValid()) {
        patientDob = parsedUsDate;
      } else {
        // Attempt 3: Try parsing with "DD-MM-YYYY"
        const parsedEuDate = dayjs(dobString, "DD-MM-YYYY");
        if (parsedEuDate.isValid()) {
          patientDob = parsedEuDate;
        } else {
          // If all attempts fail, patientDob remains null, and a warning is logged.
          console.warn(
            `DOB string "${dobString}" could not be parsed into a valid date using common formats (ISO, MM/DD/YYYY, DD-MM-YYYY).`
          );
        }
      }
    }
  } else if (dobString) {
    console.warn(
      `Received DOB ("${dobString}") is not a non-empty string or is in an unexpected format.`
    );
  }

  const { mutate } = useMutation<
    AxiosResponse<GenerateReportSuccessResponse>,
    AxiosError<ApiErrorData>,
    GenerateReportPayload
  >({
    mutationFn: generateReport,
    onSuccess: (res) => {
      toast.success(res.data?.message || "Report generated successfully!");
      toggleDrawer(false);
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
    onError: (error: AxiosError<ApiErrorData>) => {
      const errorMessage = error.response?.data?.message;
      if (Array.isArray(errorMessage) && errorMessage.length > 0) {
        toast.error(errorMessage[0]);
      } else if (typeof errorMessage === "string") {
        toast.error(errorMessage);
      } else {
        toast.error(error.message || "Failed to generate report.");
      }
    },
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ReportFormData>({
    defaultValues: {
      medicalRecordNumber: autoGeneratedMRN,
      mainConcern: "",
      procedures: null,
      findings: "",
      behavior: null,
      attention: null,
      eyeContact: null,
      howProcedure: "",
      reinforcement: "",
      generalImpression: "",
      recommendations: "",
      planNextSession: "",
    },
  });

  const getErrorMessage = (
    error: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined
  ): string | undefined => {
    if (!error) return undefined;
    if (typeof error.message === "string") return error.message;
    return undefined;
  };

  const onSubmit = (formData: ReportFormData) => {
    if (!reportDate || !reportDate.isValid()) {
      toast.error("Please select a valid 'From Date'.");
      return;
    }
    if (!sessionId) {
      toast.error("Session ID is missing.");
      return;
    }
    const formattedReportDate = reportDate.format("YYYY-MM-DD");

    const submissionPayload: GenerateReportPayload = {
      data: { ...formData, date: formattedReportDate },
      type: "MANUAL",
      sessionId: sessionId,
    };
    mutate(submissionPayload);
  };

  const proceduresOptions = [
    "Diagnostic",
    "Treatment",
    "Assessment",
    "consultation",
  ];
  const behaviorOptions = ["Cooperative", "Uncooperative"];
  const impressionOptions = ["Good", "Fair", "Poor"];

  return (
    <Box width={{ xs: "100%", sm: "500px" }} p={2}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column" gap="20px">
          <TextField
            label="Medical Record Number"
            variant="standard"
            value={autoGeneratedMRN}
            InputProps={{ readOnly: true }}
            disabled
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
              },
              backgroundColor: "action.disabledBackground",
            }}
          />

          <FormControl component="fieldset" fullWidth={true}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={reportDate}
                maxDate={dayjs()}
                onChange={(newValue: Dayjs | null) => {
                  setReportDate(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant={"standard"}
                    fullWidth={true}
                    required={true}
                    error={!reportDate}
                    helperText={
                      !reportDate ? "From Date is required" : params.helperText
                    }
                  />
                )}
              />
            </LocalizationProvider>
          </FormControl>
          <TextField
            label="Name"
            variant="standard"
            value={data?.patientProfile?.fullName || ""}
            InputProps={{ readOnly: true }}
            disabled
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
              },
              backgroundColor: "action.disabledBackground",
            }}
          />
          <TextField
            label="Session Number"
            variant="standard"
            value={data?.sessionId || ""}
            InputProps={{ readOnly: true }}
            disabled
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
              },
              backgroundColor: "action.disabledBackground",
            }}
          />

          {/* Date of Birth Field */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date of birth"
              value={patientDob}
              onChange={() => {}}
              readOnly
              disabled
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant={"standard"}
                  fullWidth={true}
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                    },
                    backgroundColor: patientDob
                      ? "action.disabledBackground"
                      : "transparent",
                  }}
                />
              )}
            />
          </LocalizationProvider>

          <TextField
            label="Age"
            variant="standard"
            value={data?.patientProfile?.age || ""}
            InputProps={{ readOnly: true }}
            disabled
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
              },
              backgroundColor: "action.disabledBackground",
            }}
          />

          <TextField
            label="Main Concern *"
            multiline
            maxRows={3}
            variant="standard"
            {...register("mainConcern", {
              required: "Main Concern is required",
            })}
            error={Boolean(errors.mainConcern)}
            helperText={getErrorMessage(errors.mainConcern)}
          />

          <Controller
            name="procedures"
            control={control}
            rules={{ required: "Procedures is required" }}
            render={({ field, fieldState }) => (
              <Autocomplete
                {...field}
                options={proceduresOptions}
                onChange={(_, newValue) => field.onChange(newValue)}
                value={field.value || null}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <TextField
                    {...params}
                    label="Procedures *"
                    variant="standard"
                    error={Boolean(fieldState.error)}
                    helperText={getErrorMessage(fieldState.error)}
                  />
                )}
              />
            )}
          />

          <TextField
            label="Findings *"
            variant="standard"
            {...register("findings", {
              required: "Findings is required",
            })}
            error={Boolean(errors.findings)}
            helperText={getErrorMessage(errors.findings)}
          />

          <Controller
            name="behavior"
            control={control}
            rules={{ required: "Behavior is required" }}
            render={({ field, fieldState }) => (
              <Autocomplete
                {...field}
                options={behaviorOptions}
                onChange={(_, newValue) => field.onChange(newValue)}
                value={field.value || null}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <TextField
                    {...params}
                    label="Behavior *"
                    variant="standard"
                    error={Boolean(fieldState.error)}
                    helperText={getErrorMessage(fieldState.error)}
                  />
                )}
              />
            )}
          />

          <Controller
            name="attention"
            control={control}
            rules={{ required: "Attention is required" }}
            render={({ field, fieldState }) => (
              <Autocomplete
                {...field}
                options={impressionOptions}
                onChange={(_, newValue) => field.onChange(newValue)}
                value={field.value || null}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <TextField
                    {...params}
                    label="Attention *"
                    variant="standard"
                    error={Boolean(fieldState.error)}
                    helperText={getErrorMessage(fieldState.error)}
                  />
                )}
              />
            )}
          />

          <Controller
            name="eyeContact"
            control={control}
            rules={{ required: "Eye Contact is required" }}
            render={({ field, fieldState }) => (
              <Autocomplete
                {...field}
                options={impressionOptions}
                onChange={(_, newValue) => field.onChange(newValue)}
                value={field.value || null}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <TextField
                    {...params}
                    label="Eye Contact *"
                    variant="standard"
                    error={Boolean(fieldState.error)}
                    helperText={getErrorMessage(fieldState.error)}
                  />
                )}
              />
            )}
          />

          <TextField
            label="How Procedure was done *"
            multiline
            maxRows={3}
            variant="standard"
            {...register("howProcedure", {
              required: "How Procedure was done is required",
            })}
            error={Boolean(errors.howProcedure)}
            helperText={getErrorMessage(errors.howProcedure)}
          />

          <TextField
            label="Reinforcement *"
            variant="standard"
            {...register("reinforcement", {
              required: "Reinforcement is required",
            })}
            error={Boolean(errors.reinforcement)}
            helperText={getErrorMessage(errors.reinforcement)}
          />

          <TextField
            label="General Impression *"
            variant="standard"
            {...register("generalImpression", {
              required: "General Impression is required",
            })}
            error={Boolean(errors.generalImpression)}
            helperText={getErrorMessage(errors.generalImpression)}
          />

          <TextField
            label="Recommendations *"
            multiline
            maxRows={3}
            variant="standard"
            {...register("recommendations", {
              required: "Recommendations is required",
            })}
            error={Boolean(errors.recommendations)}
            helperText={getErrorMessage(errors.recommendations)}
          />

          <TextField
            label="Plan for the next session *"
            multiline
            maxRows={3}
            variant="standard"
            {...register("planNextSession", {
              required: "Plan for the next session is required",
            })}
            error={Boolean(errors.planNextSession)}
            helperText={getErrorMessage(errors.planNextSession)}
          />

          <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }}>
            Submit
          </Button>
        </Box>
      </form>
    </Box>
  );
};
